import type { ReactNode } from 'react';

export interface CardProps {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Card — bordered surface with optional title + action slot.
 *
 * Source: .planning/v7-design-handoff/v7-sla/shell.jsx lines 239-249
 *
 * Replaces the inline `bg-white rounded-lg border border-gray-200`
 * idiom that's scattered across legacy pages; consumes v7 ms-* tokens
 * loaded by Plan 41-01 so the surface inherits the cream canvas and
 * warm border automatically.
 */
export function Card({ title, action, children, className = '' }: CardProps) {
  return (
    // MKS-493 — `rounded-ms-card` / `p-ms-card` resolve to 0.5rem / 1rem by
    // default, i.e. exactly the `rounded-lg` / `p-4` they replace. The
    // `ms-card` hook is what `styles/sparkle.css` uses for the flat 18px
    // surface and the 16px gap between cards.
    <div className={`ms-card bg-ms-surface rounded-ms-card border border-ms-border ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-ms-border">
          {title && <div className="text-[13px] font-semibold text-ms-text-strong">{title}</div>}
          {action}
        </div>
      )}
      <div className="p-ms-card">{children}</div>
    </div>
  );
}
